import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  IconButton,
  Stack,
  Collapse,
  FormControl,
  InputLabel,
  TextFieldProps,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import DashboardLayout from "../../layout";
import {
  useForm,
  useFieldArray,
  Control,
  UseFormRegister,
  FieldErrors,
  Controller,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  addManualBooking,
  AddManualBookingPayload,
  getAvailableAirlines,
  Airline,
  getAvailableAirports,
  Airport,
} from "../../services/manualBooking";
import { getAddonList } from "../../services/vehicle";
import { useNavigate, useLocation } from "react-router-dom";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LoadingState } from "../../components/LoadingState";
import { useState, forwardRef, useEffect } from "react";
import BackButton from "components/BackButton";
import { FLIGHT_CLASSES } from "../../constants/flightClasses";
import { toLocalDate, toLocalDateTime } from "utils/helpers";

// Picker values are Dates; posting them raw would serialize via toISOString()
// and shift times to UTC. Send them as typed instead.
const DATE_ONLY_KEYS = new Set([
  "DepartureDate",
  "ArrivalDate",
  "PnrDate",
  "DateOfBirth",
  "ExpiryDate",
]);

const serializeDates = (value: any, key = ""): any => {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return DATE_ONLY_KEYS.has(key) ? toLocalDate(value) : toLocalDateTime(value);
  }
  if (Array.isArray(value)) return value.map((item) => serializeDates(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, serializeDates(v, k)]),
    );
  }
  return value;
};

const schema = yup.object({
  transaction_id: yup.string().nullable(),
  status: yup.string().nullable(),
  PnrReferenceNumber: yup
    .string()
    .transform((v) => (v === "" ? null : v))
    .nullable(),
  PnrDate: yup.mixed().nullable(),
  price: yup
    .number()
    .transform((v) => (v === "" || isNaN(v) ? null : v))
    .nullable(),
  currency: yup
    .string()
    .transform((v) => (v === "" ? null : v))
    .nullable(),
  adult: yup
    .number()
    .transform((v) => (v === "" || isNaN(v) ? null : v))
    .nullable(),
  children: yup
    .number()
    .transform((v) => (v === "" || isNaN(v) ? null : v))
    .nullable(),
  infant: yup
    .number()
    .transform((v) => (v === "" || isNaN(v) ? null : v))
    .nullable(),
  contactInfo: yup.object({
    email: yup
      .string()
      .email("Invalid email")
      .transform((v) => (v === "" ? null : v))
      .nullable(),
    phoneNumber: yup
      .string()
      .transform((v) => (v === "" ? null : v))
      .nullable(),
  }),
  FlightModels: yup.array().of(
    yup.object({
      Name: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      Airline: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      AirlineName: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      DepartureCode: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      DepartureName: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      DepartureTime: yup.mixed().nullable(),
      ArrivalCode: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      ArrivalName: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      ArrivalTime: yup.mixed().nullable(),
      Stops: yup
        .number()
        .transform((v) => (v === "" || isNaN(v) ? null : v))
        .nullable(),
      StopTime: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      TripDuration: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      StopCity: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      AirlineLogoUrl: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      FreeBaggage: yup.object({
        BagCount: yup
          .number()
          .transform((v) => (v === "" || isNaN(v) ? null : v))
          .nullable(),
        Weight: yup
          .number()
          .transform((v) => (v === "" || isNaN(v) ? null : v))
          .nullable(),
        WeightUnit: yup
          .string()
          .transform((v) => (v === "" ? null : v))
          .nullable(),
      }),
      FlightLegs: yup.array().of(
        yup.object({
          FlightLegNumber: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          DepartureCode: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          DepartureName: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          DestinationCode: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          DestinationName: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          StartTime: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          EndTime: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          DepartureDate: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          DepartureTime: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          ArrivalDate: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          ArrivalTime: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          Duration: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          IsStop: yup.boolean().nullable(),
          Layover: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          LayoverDuration: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          BookingClass: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          CabinClass: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          CabinClassName: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
          Seats: yup
            .number()
            .transform((v) => (v === "" || isNaN(v) ? null : v))
            .nullable(),
          Meal: yup
            .string()
            .transform((v) => (v === "" ? null : v))
            .nullable(),
        }),
      ),
    }),
  ),
  TravellerDetails: yup.array().of(
    yup.object({
      PassengerType: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      FirstName: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      LastName: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      DateOfBirth: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      Age: yup
        .number()
        .transform((v) => (v === "" || isNaN(v) ? null : v))
        .nullable(),
      PhoneNumber: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      PassportNumber: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      ExpiryDate: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      PassportIssuingAuthority: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      PassportIssueCountryCode: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      Gender: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      Title: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      Email: yup
        .string()
        .email()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      Address: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      Country: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      CountryCode: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      City: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      PostalCode: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      TicketNumber: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      SelectedSeats: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      PassengerReferenceNumber: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
      SelectedBaggages: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
    }),
  ),
  PriceDetails: yup.array().of(
    yup.object({
      BaseFare: yup.object({
        Amount: yup
          .number()
          .transform((v) => (v === "" || isNaN(v) ? null : v))
          .nullable(),
        CurrencyCode: yup
          .string()
          .transform((v) => (v === "" ? null : v))
          .nullable(),
      }),
      PassengerType: yup
        .string()
        .transform((v) => (v === "" ? null : v))
        .nullable(),
    }),
  ),
  addons_id: yup.array().of(yup.string()),
});

// Custom Input Component to match MailForm style
const FormInput = forwardRef(
  ({ label, error, helperText, ...props }: TextFieldProps, ref) => (
    <FormControl variant="standard" fullWidth error={!!error}>
      <InputLabel
        shrink
        sx={{
          fontSize: "12px",
          color: "#344054",
          fontWeight: 600,
          mb: 1,
          position: "relative", // Changed to relative to sit above input naturally
          transform: "none",
          bgcolor: "white",
        }}
      >
        {label}
      </InputLabel>
      <TextField
        variant="standard"
        InputProps={{ disableUnderline: true }}
        sx={{
          bgcolor: "#F9F9F9",
          mt: 0,
          mb: 2,
          py: 0.5,
          px: 2,
          borderRadius: "8px",
          fontSize: "14px",
          border: "1px solid #ddd",
          "& .MuiInputBase-input::placeholder": {
            color: "#98A2B3",
            opacity: 1,
          },
          // boxShadow: "0 1px 2px 0 #1018280D",
        }}
        error={!!error}
        helperText={helperText}
        inputRef={ref}
        {...props}
      />
    </FormControl>
  ),
);

interface AirportAutocompleteProps {
  label: string;
  value: { AirportName: string; AirportCode: string } | null;
  onChange: (value: Airport | null) => void;
  error?: boolean;
  helperText?: string;
}

const AirportAutocomplete = ({
  label,
  value,
  onChange,
  error,
  helperText,
}: AirportAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [debouncedInputValue, setDebouncedInputValue] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const isSearchable = debouncedInputValue.trim().length >= 2;

  const { data, isFetching } = useQuery({
    queryKey: ["airports", debouncedInputValue],
    queryFn: ({ signal }) => getAvailableAirports(debouncedInputValue, signal),
    enabled: isSearchable,
    staleTime: 5 * 60 * 1000, // airport list barely changes; identical searches hit cache
  });

  const options = isSearchable
    ? data?.data?.airports || []
    : value
      ? [value as Airport]
      : [];

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={isFetching}
      getOptionLabel={(option) =>
        `${option.AirportName} (${option.AirportCode})`
      }
      isOptionEqualToValue={(option, value) =>
        option.AirportName === value.AirportName
      }
      filterOptions={(x) => x}
      value={value as Airport}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={(_, newInputValue, reason) => {
        // 'reset' fires when MUI syncs the text to a just-selected option's
        // label — searching for that full label again is wasted work.
        if (reason === "reset") return;
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <FormControl variant="standard" fullWidth error={error}>
          <InputLabel
            shrink
            sx={{
              fontSize: "12px",
              color: "#344054",
              fontWeight: 600,
              mb: 1,
              position: "relative",
              transform: "none",
              bgcolor: "white",
            }}
          >
            {label}
          </InputLabel>
          <TextField
            {...params}
            variant="standard"
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              endAdornment: (
                <>
                  {isFetching ? (
                    <Typography variant="caption">Loading...</Typography>
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              bgcolor: "#F9F9F9",
              mt: 0,
              mb: 2,
              py: 0.5,
              px: 2,
              borderRadius: "8px",
              fontSize: "14px",
              border: "1px solid #ddd",
              "& .MuiInputBase-input::placeholder": {
                color: "#98A2B3",
                opacity: 1,
              },
            }}
          />
        </FormControl>
      )}
    />
  );
};

interface Addon {
  _id: string;
  name: string;
  price: number;
  description: string;
  is_default: boolean;
}

interface AddonAutocompleteProps {
  label: string;
  value: string | null;
  onChange: (value: Addon | null) => void;
  error?: boolean;
  helperText?: string;
}

const AddonAutocomplete = ({
  label,
  value,
  onChange,
  error,
  helperText,
}: AddonAutocompleteProps) => {
  const { data: addonsData, isLoading } = useQuery({
    queryKey: ["addons"],
    queryFn: getAddonList,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const addons: Addon[] = addonsData?.data?.addons || [];
  const selectedAddon = addons.find((a) => a._id === value) || null;

  return (
    <Autocomplete
      fullWidth
      options={addons}
      loading={isLoading}
      getOptionLabel={(option) => `${option.name} - ₦${option.price}`}
      isOptionEqualToValue={(option, val) => option._id === val._id}
      value={selectedAddon}
      onChange={(_, newValue) => onChange(newValue)}
      renderInput={(params) => (
        <FormControl variant="standard" fullWidth error={error}>
          <InputLabel
            shrink
            sx={{
              fontSize: "12px",
              color: "#344054",
              fontWeight: 600,
              mb: 1,
              position: "relative",
              transform: "none",
              bgcolor: "white",
            }}
          >
            {label}
          </InputLabel>
          <TextField
            {...params}
            variant="standard"
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              endAdornment: (
                <>
                  {isLoading ? (
                    <Typography variant="caption">Loading...</Typography>
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              bgcolor: "#F9F9F9",
              mt: 0,
              mb: 2,
              py: 0.5,
              px: 2,
              borderRadius: "8px",
              fontSize: "14px",
              border: "1px solid #ddd",
              "& .MuiInputBase-input::placeholder": {
                color: "#98A2B3",
                opacity: 1,
              },
            }}
          />
        </FormControl>
      )}
    />
  );
};

interface FlightLegItemProps {
  control: Control<AddManualBookingPayload>;
  parentIndex: number;
  legIndex: number;
  register: UseFormRegister<AddManualBookingPayload>;
  remove: (index: number) => void;
  setValue: UseFormSetValue<AddManualBookingPayload>;
}

const FlightLegItem = ({
  control,
  parentIndex,
  legIndex,
  register,
  remove,
  setValue,
}: FlightLegItemProps) => {
  const depName = useWatch({
    control,
    name: `FlightModels.${parentIndex}.FlightLegs.${legIndex}.DepartureName`,
  });
  const depCode = useWatch({
    control,
    name: `FlightModels.${parentIndex}.FlightLegs.${legIndex}.DepartureCode`,
  });
  const destName = useWatch({
    control,
    name: `FlightModels.${parentIndex}.FlightLegs.${legIndex}.DestinationName`,
  });
  const destCode = useWatch({
    control,
    name: `FlightModels.${parentIndex}.FlightLegs.${legIndex}.DestinationCode`,
  });

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        bgcolor: "#fff",
        border: "1px solid #eee",
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="caption" fontWeight="bold">
          Leg #{legIndex + 1}
        </Typography>
        <IconButton size="small" color="error" onClick={() => remove(legIndex)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormInput
            label="Leg Number"
            {...register(
              `FlightModels.${parentIndex}.FlightLegs.${legIndex}.FlightLegNumber`,
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <AirportAutocomplete
            label="Departure Name"
            value={
              depName
                ? { AirportName: depName, AirportCode: depCode || "" }
                : null
            }
            onChange={(newValue) => {
              setValue(
                `FlightModels.${parentIndex}.FlightLegs.${legIndex}.DepartureName`,
                newValue?.AirportName || null,
              );
              setValue(
                `FlightModels.${parentIndex}.FlightLegs.${legIndex}.DepartureCode`,
                newValue?.AirportCode || null,
              );
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <AirportAutocomplete
            label="Destination Name"
            value={
              destName
                ? { AirportName: destName, AirportCode: destCode || "" }
                : null
            }
            onChange={(newValue) => {
              setValue(
                `FlightModels.${parentIndex}.FlightLegs.${legIndex}.DestinationName`,
                newValue?.AirportName || null,
              );
              setValue(
                `FlightModels.${parentIndex}.FlightLegs.${legIndex}.DestinationCode`,
                newValue?.AirportCode || null,
              );
            }}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Controller
            name={`FlightModels.${parentIndex}.FlightLegs.${legIndex}.StartTime`}
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel
                    shrink
                    sx={{
                      fontSize: "12px",
                      color: "#344054",
                      fontWeight: 600,
                      mb: 1,
                      position: "relative",
                      transform: "none",
                      bgcolor: "white",
                    }}
                  >
                    Start Time
                  </InputLabel>
                  <DateTimePicker
                    {...field}
                    slots={{
                      textField: TextField,
                    }}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        InputProps: { disableUnderline: true },
                        sx: {
                          bgcolor: "#F9F9F9",
                          mt: 0,
                          mb: 2,
                          py: 0.5,
                          px: 2,
                          borderRadius: "8px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          "& .MuiInputBase-input::placeholder": {
                            color: "#98A2B3",
                            opacity: 1,
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Controller
            name={`FlightModels.${parentIndex}.FlightLegs.${legIndex}.EndTime`}
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel
                    shrink
                    sx={{
                      fontSize: "12px",
                      color: "#344054",
                      fontWeight: 600,
                      mb: 1,
                      position: "relative",
                      transform: "none",
                      bgcolor: "white",
                    }}
                  >
                    End Time
                  </InputLabel>
                  <DateTimePicker
                    {...field}
                    slots={{
                      textField: TextField,
                    }}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        InputProps: { disableUnderline: true },
                        sx: {
                          bgcolor: "#F9F9F9",
                          mt: 0,
                          mb: 2,
                          py: 0.5,
                          px: 2,
                          borderRadius: "8px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          "& .MuiInputBase-input::placeholder": {
                            color: "#98A2B3",
                            opacity: 1,
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormInput
            label="Duration"
            {...register(
              `FlightModels.${parentIndex}.FlightLegs.${legIndex}.Duration`,
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Controller
            name={`FlightModels.${parentIndex}.FlightLegs.${legIndex}.DepartureDate`}
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel
                    shrink
                    sx={{
                      fontSize: "12px",
                      color: "#344054",
                      fontWeight: 600,
                      mb: 1,
                      position: "relative",
                      transform: "none",
                      bgcolor: "white",
                    }}
                  >
                    Departure Date
                  </InputLabel>
                  <DatePicker
                    {...field}
                    slots={{
                      textField: TextField,
                    }}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        InputProps: { disableUnderline: true },
                        sx: {
                          bgcolor: "#F9F9F9",
                          mt: 0,
                          mb: 2,
                          py: 0.5,
                          px: 2,
                          borderRadius: "8px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          "& .MuiInputBase-input::placeholder": {
                            color: "#98A2B3",
                            opacity: 1,
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Controller
            name={`FlightModels.${parentIndex}.FlightLegs.${legIndex}.DepartureTime`}
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel
                    shrink
                    sx={{
                      fontSize: "12px",
                      color: "#344054",
                      fontWeight: 600,
                      mb: 1,
                      position: "relative",
                      transform: "none",
                      bgcolor: "white",
                    }}
                  >
                    Departure Time
                  </InputLabel>
                  <DateTimePicker
                    {...field}
                    slots={{
                      textField: TextField,
                    }}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        InputProps: { disableUnderline: true },
                        sx: {
                          bgcolor: "#F9F9F9",
                          mt: 0,
                          mb: 2,
                          py: 0.5,
                          px: 2,
                          borderRadius: "8px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          "& .MuiInputBase-input::placeholder": {
                            color: "#98A2B3",
                            opacity: 1,
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Controller
            name={`FlightModels.${parentIndex}.FlightLegs.${legIndex}.ArrivalDate`}
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel
                    shrink
                    sx={{
                      fontSize: "12px",
                      color: "#344054",
                      fontWeight: 600,
                      mb: 1,
                      position: "relative",
                      transform: "none",
                      bgcolor: "white",
                    }}
                  >
                    Arrival Date
                  </InputLabel>
                  <DatePicker
                    {...field}
                    slots={{
                      textField: TextField,
                    }}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        InputProps: { disableUnderline: true },
                        sx: {
                          bgcolor: "#F9F9F9",
                          mt: 0,
                          mb: 2,
                          py: 0.5,
                          px: 2,
                          borderRadius: "8px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          "& .MuiInputBase-input::placeholder": {
                            color: "#98A2B3",
                            opacity: 1,
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Controller
            name={`FlightModels.${parentIndex}.FlightLegs.${legIndex}.ArrivalTime`}
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel
                    shrink
                    sx={{
                      fontSize: "12px",
                      color: "#344054",
                      fontWeight: 600,
                      mb: 1,
                      position: "relative",
                      transform: "none",
                      bgcolor: "white",
                    }}
                  >
                    Arrival Time
                  </InputLabel>
                  <DateTimePicker
                    {...field}
                    slots={{
                      textField: TextField,
                    }}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        InputProps: { disableUnderline: true },
                        sx: {
                          bgcolor: "#F9F9F9",
                          mt: 0,
                          mb: 2,
                          py: 0.5,
                          px: 2,
                          borderRadius: "8px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          "& .MuiInputBase-input::placeholder": {
                            color: "#98A2B3",
                            opacity: 1,
                          },
                        },
                      },
                    }}
                  />
                </FormControl>
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormInput
            label="Layover"
            {...register(
              `FlightModels.${parentIndex}.FlightLegs.${legIndex}.Layover`,
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormInput
            label="Layover Duration"
            {...register(
              `FlightModels.${parentIndex}.FlightLegs.${legIndex}.LayoverDuration`,
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            control={control}
            name={`FlightModels.${parentIndex}.FlightLegs.${legIndex}.CabinClass`}
            render={({ field }) => (
              <FormInput
                select
                label="Cabin / Class"
                {...field}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  const selectedOption = FLIGHT_CLASSES.find(
                    (c) => c.value === selectedValue,
                  );

                  field.onChange(selectedValue);

                  if (selectedOption) {
                    setValue(
                      `FlightModels.${parentIndex}.FlightLegs.${legIndex}.BookingClass`,
                      selectedValue,
                    );
                    setValue(
                      `FlightModels.${parentIndex}.FlightLegs.${legIndex}.CabinClassName`,
                      selectedOption.label,
                    );
                  }
                }}
              >
                {FLIGHT_CLASSES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </FormInput>
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormInput
            label="Seats"
            {...register(
              `FlightModels.${parentIndex}.FlightLegs.${legIndex}.Seats`,
            )}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormInput
            label="Meal"
            {...register(
              `FlightModels.${parentIndex}.FlightLegs.${legIndex}.Meal`,
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                {...register(
                  `FlightModels.${parentIndex}.FlightLegs.${legIndex}.IsStop`,
                )}
                style={{ marginRight: "8px" }}
              />
              <Typography variant="body2">Is Stop</Typography>
            </Box>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

interface FlightModelItemProps {
  control: Control<AddManualBookingPayload>;
  index: number;
  register: UseFormRegister<AddManualBookingPayload>;
  remove: (index: number) => void;
  errors: FieldErrors<AddManualBookingPayload>;
  availableAirlines?: Airline[];
  setValue: UseFormSetValue<AddManualBookingPayload>;
}

const FlightModelItem = ({
  control,
  index,
  register,
  remove,
  errors,
  availableAirlines,
  setValue,
}: FlightModelItemProps) => {
  const airlineLogoUrl = useWatch({
    control,
    name: `FlightModels.${index}.AirlineLogoUrl`,
  });

  const depName = useWatch({
    control,
    name: `FlightModels.${index}.DepartureName`,
  });
  const depCode = useWatch({
    control,
    name: `FlightModels.${index}.DepartureCode`,
  });
  const arrName = useWatch({
    control,
    name: `FlightModels.${index}.ArrivalName`,
  });
  const arrCode = useWatch({
    control,
    name: `FlightModels.${index}.ArrivalCode`,
  });

  const {
    fields: legs,
    append: appendLeg,
    remove: removeLeg,
  } = useFieldArray({
    control,
    name: `FlightModels.${index}.FlightLegs`,
  });

  const [expanded, setExpanded] = useState(true);

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          borderBottom: "1px solid #EAECF0",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            <ExpandMoreIcon
              sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} color="#101828">
            Flight #{index + 1}
          </Typography>
        </Box>
        <IconButton onClick={() => remove(index)} color="error" size="small">
          <DeleteOutlineIcon />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              label="Name"
              {...register(`FlightModels.${index}.Name`)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              control={control}
              name={`FlightModels.${index}.AirlineName`}
              render={({ field: { value } }) => (
                <Autocomplete
                  options={availableAirlines || []}
                  getOptionLabel={(option) => option.AirlineName}
                  isOptionEqualToValue={(option, value) =>
                    option.AirlineName === value.AirlineName
                  }
                  value={
                    availableAirlines?.find((a) => a.AirlineName === value) ||
                    null
                  }
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setValue(
                        `FlightModels.${index}.Airline`,
                        newValue.Airline,
                      );
                      setValue(
                        `FlightModels.${index}.AirlineName`,
                        newValue.AirlineName,
                      );
                      setValue(
                        `FlightModels.${index}.AirlineLogoUrl`,
                        newValue.AirlineLogoUrl,
                      );
                    } else {
                      setValue(`FlightModels.${index}.Airline`, null);
                      setValue(`FlightModels.${index}.AirlineName`, null);
                      setValue(`FlightModels.${index}.AirlineLogoUrl`, null);
                    }
                  }}
                  renderInput={(params) => (
                    <FormControl variant="standard" fullWidth>
                      <InputLabel
                        shrink
                        sx={{
                          fontSize: "12px",
                          color: "#344054",
                          fontWeight: 600,
                          mb: 1,
                          position: "relative",
                          transform: "none",
                          bgcolor: "white",
                        }}
                      >
                        Airline
                      </InputLabel>
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                        sx={{
                          bgcolor: "#F9F9F9",
                          mt: 0,
                          mb: 2,
                          py: 0.5,
                          px: 2,
                          borderRadius: "8px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          "& .MuiInputBase-input::placeholder": {
                            color: "#98A2B3",
                            opacity: 1,
                          },
                        }}
                      />
                    </FormControl>
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <Box
                        component="img"
                        src={option.AirlineLogoUrl}
                        alt={option.AirlineName}
                        sx={{ width: 24, height: 24, objectFit: "contain" }}
                      />
                      {option.AirlineName} ({option.Airline})
                    </Box>
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AirportAutocomplete
              label="Departure Name"
              value={
                depName
                  ? { AirportName: depName, AirportCode: depCode || "" }
                  : null
              }
              onChange={(newValue) => {
                setValue(
                  `FlightModels.${index}.DepartureName`,
                  newValue?.AirportName || null,
                );
                setValue(
                  `FlightModels.${index}.DepartureCode`,
                  newValue?.AirportCode || null,
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AirportAutocomplete
              label="Arrival Name"
              value={
                arrName
                  ? { AirportName: arrName, AirportCode: arrCode || "" }
                  : null
              }
              onChange={(newValue) => {
                setValue(
                  `FlightModels.${index}.ArrivalName`,
                  newValue?.AirportName || null,
                );
                setValue(
                  `FlightModels.${index}.ArrivalCode`,
                  newValue?.AirportCode || null,
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name={`FlightModels.${index}.DepartureTime`}
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel
                      shrink
                      sx={{
                        fontSize: "12px",
                        color: "#344054",
                        fontWeight: 600,
                        mb: 1,
                        position: "relative",
                        transform: "none",
                        bgcolor: "white",
                      }}
                    >
                      Departure Time
                    </InputLabel>
                    <DateTimePicker
                      {...field}
                      slots={{
                        textField: TextField,
                      }}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          InputProps: { disableUnderline: true },
                          sx: {
                            bgcolor: "#F9F9F9",
                            mt: 0,
                            mb: 2,
                            py: 0.5,
                            px: 2,
                            borderRadius: "8px",
                            fontSize: "14px",
                            border: "1px solid #ddd",
                            "& .MuiInputBase-input::placeholder": {
                              color: "#98A2B3",
                              opacity: 1,
                            },
                          },
                        },
                      }}
                    />
                  </FormControl>
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name={`FlightModels.${index}.ArrivalTime`}
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel
                      shrink
                      sx={{
                        fontSize: "12px",
                        color: "#344054",
                        fontWeight: 600,
                        mb: 1,
                        position: "relative",
                        transform: "none",
                        bgcolor: "white",
                      }}
                    >
                      Arrival Time
                    </InputLabel>
                    <DateTimePicker
                      {...field}
                      slots={{
                        textField: TextField,
                      }}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          InputProps: { disableUnderline: true },
                          sx: {
                            bgcolor: "#F9F9F9",
                            mt: 0,
                            mb: 2,
                            py: 0.5,
                            px: 2,
                            borderRadius: "8px",
                            fontSize: "14px",
                            border: "1px solid #ddd",
                            "& .MuiInputBase-input::placeholder": {
                              color: "#98A2B3",
                              opacity: 1,
                            },
                          },
                        },
                      }}
                    />
                  </FormControl>
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              label="Stops"
              type="number"
              {...register(`FlightModels.${index}.Stops`)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              label="Stop Time"
              {...register(`FlightModels.${index}.StopTime`)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              label="Trip Duration"
              {...register(`FlightModels.${index}.TripDuration`)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              label="Stop City"
              {...register(`FlightModels.${index}.StopCity`)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            {airlineLogoUrl && (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  pl: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="textSecondary"
                >
                  Airline Logo:
                </Typography>
                <Box
                  component="img"
                  src={airlineLogoUrl}
                  alt="Airline Logo"
                  sx={{ height: 40, objectFit: "contain" }}
                />
              </Box>
            )}
          </Grid>

          {/* Baggage */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
              Free Baggage
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormInput
              label="Count"
              type="number"
              {...register(`FlightModels.${index}.FreeBaggage.BagCount`)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormInput
              label="Weight"
              type="number"
              {...register(`FlightModels.${index}.FreeBaggage.Weight`)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormInput
              label="Unit"
              {...register(`FlightModels.${index}.FreeBaggage.WeightUnit`)}
            />
          </Grid>
        </Grid>

        {/* Nested Flight Legs */}
        <Box sx={{ mt: 3, pl: 2, borderLeft: "2px solid #ddd" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="subtitle2">Flight Legs</Typography>
            <Button
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() =>
                appendLeg({
                  FlightLegNumber: "",
                  DepartureCode: "",
                  DepartureName: "",
                  DestinationCode: "",
                  DestinationName: "",
                  StartTime: "",
                  EndTime: "",
                  Duration: "",
                  IsStop: false,
                  LayoverDuration: "",
                  BookingClass: "",
                  CabinClass: "",
                  CabinClassName: "",
                  DepartureDate: null,
                  DepartureTime: null,
                  ArrivalDate: null,
                  ArrivalTime: null,
                  Layover: null,
                  Seats: null,
                  Meal: null,
                })
              }
            >
              Add Leg
            </Button>
          </Box>

          {legs.map((leg, legIndex) => (
            <FlightLegItem
              key={leg.id}
              control={control}
              parentIndex={index}
              legIndex={legIndex}
              register={register}
              remove={removeLeg}
              setValue={setValue}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const AddManualBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<AddManualBookingPayload>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      transaction_id: "",
      status: "booked_successfully",
      PnrReferenceNumber: "",
      PnrDate: null as any,
      price: 0,
      currency: "NGN",
      adult: 1,
      children: 0,
      infant: 0,
      contactInfo: {
        email: "",
        phoneNumber: "",
      },
      FlightModels: [],
      TravellerDetails: [],
      PriceDetails: [],
      addons_id: [],
    },
  });

  const {
    fields: flightModels,
    append: appendFlight,
    remove: removeFlight,
  } = useFieldArray({
    control,
    name: "FlightModels",
  });

  const {
    fields: travellers,
    append: appendTraveller,
    remove: removeTraveller,
  } = useFieldArray({
    control,
    name: "TravellerDetails",
  });

  const {
    fields: prices,
    append: appendPrice,
    remove: removePrice,
  } = useFieldArray({
    control,
    name: "PriceDetails",
  });

  const {
    fields: addons,
    append: appendAddon,
    remove: removeAddon,
  } = useFieldArray({
    control,
    name: "addons_id" as any,
  });

  const watchedCurrency = watch("currency");

  useEffect(() => {
    if (location.state?.transactionId) {
      setValue("transaction_id", location.state.transactionId);
    }
  }, [location.state, setValue]);

  useEffect(() => {
    if (watchedCurrency) {
      prices.forEach((_, index) => {
        setValue(
          `PriceDetails.${index}.BaseFare.CurrencyCode`,
          watchedCurrency,
        );
      });
    }
  }, [watchedCurrency, prices, setValue]);

  const { data: availableAirlines } = useQuery({
    queryKey: ["availableAirlines"],
    queryFn: getAvailableAirlines,
    staleTime: 5 * 60 * 1000, // reference/lookup data — refetch at most every 5 min
  });

  const mutation = useMutation({
    mutationFn: addManualBooking,
    onSuccess: () => {
      setSuccessMsg("Booking added successfully!");
      setTimeout(() => {
        navigate("/dashboard/manual-bookings");
      }, 2000);
    },
    onError: (error: any) => {
      setErrorMsg(error?.response?.data?.message || "Failed to add booking");
    },
  });

  const onSubmit = (data: AddManualBookingPayload) => {
    mutation.mutate(serializeDates(data));
  };

  return (
    <DashboardLayout nextSideBar>
      {mutation.isPending && <LoadingState />}
      <Box sx={{ background: "white", mt: "-38px", mx: "-22px", mb: "30px" }}>
        <BackButton />
      </Box>

      <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
        <Box mb={3}>
          <Typography variant="h5" fontWeight={600} sx={{ fontSize: "24px" }}>
            Create Manual Booking
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <form
            onSubmit={handleSubmit(onSubmit, (errors) => console.log(errors))}
          >
            <Stack spacing={4}>
              {/* General Info */}
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "20px", mb: 3 }}>
                  Booking Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormInput
                      label="Transaction Id"
                      {...register("transaction_id")}
                      error={!!errors.transaction_id}
                      helperText={errors.transaction_id?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInput
                      select
                      label="Booking Status"
                      {...register("status")}
                      defaultValue="completed"
                      error={!!errors.status}
                      helperText={errors.status?.message}
                    >
                      <MenuItem value="booked_successfully">Success</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </FormInput>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormInput
                      label="PNR Reference Number"
                      {...register("PnrReferenceNumber")}
                      error={!!errors.PnrReferenceNumber}
                      helperText={errors.PnrReferenceNumber?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="PnrDate"
                      control={control}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <FormControl
                            variant="standard"
                            fullWidth
                            error={!!errors.PnrDate}
                          >
                            <InputLabel
                              shrink
                              sx={{
                                fontSize: "12px",
                                color: "#344054",
                                fontWeight: 600,
                                mb: 1,
                                position: "relative",
                                transform: "none",
                                bgcolor: "white",
                              }}
                            >
                              PNR Issue Date
                            </InputLabel>
                            <DatePicker
                              {...field}
                              slots={{
                                textField: TextField,
                              }}
                              slotProps={{
                                textField: {
                                  variant: "standard",
                                  InputProps: { disableUnderline: true },
                                  sx: {
                                    bgcolor: "#F9F9F9",
                                    mt: 0,
                                    mb: 2,
                                    py: 0.5,
                                    px: 2,
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                    "& .MuiInputBase-input::placeholder": {
                                      color: "#98A2B3",
                                      opacity: 1,
                                    },
                                  },
                                  error: !!errors.PnrDate,
                                  helperText: errors.PnrDate?.message,
                                },
                              }}
                            />
                          </FormControl>
                        </LocalizationProvider>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Contact Info */}
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "20px", mb: 3 }}>
                  Customer Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormInput
                      label="Contact Email"
                      {...register("contactInfo.email")}
                      error={!!errors.contactInfo?.email}
                      helperText={errors.contactInfo?.email?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInput
                      label="Contact Phone Number"
                      {...register("contactInfo.phoneNumber")}
                      error={!!errors.contactInfo?.phoneNumber}
                      helperText={errors.contactInfo?.phoneNumber?.message}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Passenger Counts */}
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "20px", mb: 3 }}>
                  Passenger Counts
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <FormInput
                      label="Adults"
                      type="number"
                      {...register("adult")}
                      error={!!errors.adult}
                      helperText={errors.adult?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormInput
                      label="Children"
                      type="number"
                      {...register("children")}
                      error={!!errors.children}
                      helperText={errors.children?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormInput
                      label="Infants"
                      type="number"
                      {...register("infant")}
                      error={!!errors.infant}
                      helperText={errors.infant?.message}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Payment Info */}
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "20px", mb: 3 }}>
                  Pricing Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormInput
                      label="Total Price"
                      type="number"
                      {...register("price")}
                      error={!!errors.price}
                      helperText={errors.price?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormInput
                      select
                      label="Currency"
                      {...register("currency")}
                      defaultValue="NGN"
                      error={!!errors.currency}
                      helperText={errors.currency?.message}
                    >
                      <MenuItem value="NGN">NGN</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="GBP">GBP</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                    </FormInput>
                  </Grid>
                </Grid>
              </Box>

              {/* Traveller Details */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>
                    Traveller Details
                  </Typography>
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() =>
                      appendTraveller({
                        PassengerType: "Adult",
                        FirstName: "",
                        LastName: "",
                        DateOfBirth: "",
                        Age: null,
                        PhoneNumber: "",
                        PassportNumber: null,
                        ExpiryDate: null,
                        PassportIssuingAuthority: null,
                        PassportIssueCountryCode: null,
                        Gender: "Male",
                        Title: "Mr",
                        Email: "",
                        Address: null,
                        Country: "Nigeria",
                        CountryCode: "NG",
                        City: "",
                        PostalCode: "",
                        TicketNumber: null,
                        SelectedSeats: null,
                        PassengerReferenceNumber: null,
                        SelectedBaggages: null,
                      })
                    }
                  >
                    Add Traveller
                  </Button>
                </Box>
                {travellers.map((field, index) => (
                  <Box key={field.id} sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                        borderBottom: "1px solid #EAECF0",
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        color="#101828"
                      >
                        Traveller #{index + 1}
                      </Typography>
                      <IconButton
                        onClick={() => removeTraveller(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          select
                          label="Passenger Type"
                          {...register(
                            `TravellerDetails.${index}.PassengerType`,
                          )}
                          defaultValue={field.PassengerType || "Adult"}
                        >
                          <MenuItem value="Adult">Adult</MenuItem>
                          <MenuItem value="Child">Child</MenuItem>
                          <MenuItem value="Infant">Infant</MenuItem>
                        </FormInput>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Title"
                          {...register(`TravellerDetails.${index}.Title`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="First Name"
                          {...register(`TravellerDetails.${index}.FirstName`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Last Name"
                          {...register(`TravellerDetails.${index}.LastName`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name={`TravellerDetails.${index}.DateOfBirth`}
                          control={control}
                          render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <FormControl variant="standard" fullWidth>
                                <InputLabel
                                  shrink
                                  sx={{
                                    fontSize: "12px",
                                    color: "#344054",
                                    fontWeight: 600,
                                    mb: 1,
                                    position: "relative",
                                    transform: "none",
                                    bgcolor: "white",
                                  }}
                                >
                                  Date of Birth
                                </InputLabel>
                                <DatePicker
                                  {...field}
                                  slots={{
                                    textField: TextField,
                                  }}
                                  slotProps={{
                                    textField: {
                                      variant: "standard",
                                      InputProps: { disableUnderline: true },
                                      sx: {
                                        bgcolor: "#F9F9F9",
                                        mt: 0,
                                        mb: 2,
                                        py: 0.5,
                                        px: 2,
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                        "& .MuiInputBase-input::placeholder": {
                                          color: "#98A2B3",
                                          opacity: 1,
                                        },
                                      },
                                    },
                                  }}
                                />
                              </FormControl>
                            </LocalizationProvider>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Age"
                          type="number"
                          {...register(`TravellerDetails.${index}.Age`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Gender"
                          {...register(`TravellerDetails.${index}.Gender`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Email"
                          {...register(`TravellerDetails.${index}.Email`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Phone Number"
                          {...register(`TravellerDetails.${index}.PhoneNumber`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Address"
                          {...register(`TravellerDetails.${index}.Address`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="City"
                          {...register(`TravellerDetails.${index}.City`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Postal Code"
                          {...register(`TravellerDetails.${index}.PostalCode`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Country"
                          {...register(`TravellerDetails.${index}.Country`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Country Code"
                          {...register(`TravellerDetails.${index}.CountryCode`)}
                        />
                      </Grid>

                      {/* Passport Details */}
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Passport Number"
                          {...register(
                            `TravellerDetails.${index}.PassportNumber`,
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name={`TravellerDetails.${index}.ExpiryDate`}
                          control={control}
                          render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <FormControl variant="standard" fullWidth>
                                <InputLabel
                                  shrink
                                  sx={{
                                    fontSize: "12px",
                                    color: "#344054",
                                    fontWeight: 600,
                                    mb: 1,
                                    position: "relative",
                                    transform: "none",
                                    bgcolor: "white",
                                  }}
                                >
                                  Passport Expiry Date
                                </InputLabel>
                                <DatePicker
                                  {...field}
                                  slots={{
                                    textField: TextField,
                                  }}
                                  slotProps={{
                                    textField: {
                                      variant: "standard",
                                      InputProps: { disableUnderline: true },
                                      sx: {
                                        bgcolor: "#F9F9F9",
                                        mt: 0,
                                        mb: 2,
                                        py: 0.5,
                                        px: 2,
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                        "& .MuiInputBase-input::placeholder": {
                                          color: "#98A2B3",
                                          opacity: 1,
                                        },
                                      },
                                    },
                                  }}
                                />
                              </FormControl>
                            </LocalizationProvider>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Passport Issuing Authority"
                          {...register(
                            `TravellerDetails.${index}.PassportIssuingAuthority`,
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Passport Issue Country Code"
                          {...register(
                            `TravellerDetails.${index}.PassportIssueCountryCode`,
                          )}
                        />
                      </Grid>

                      {/* Booking Details */}
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Ticket Number"
                          {...register(
                            `TravellerDetails.${index}.TicketNumber`,
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Passenger Reference Number"
                          {...register(
                            `TravellerDetails.${index}.PassengerReferenceNumber`,
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Selected Seats"
                          {...register(
                            `TravellerDetails.${index}.SelectedSeats`,
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormInput
                          label="Selected Baggages"
                          {...register(
                            `TravellerDetails.${index}.SelectedBaggages`,
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>

              {/* Flight Models */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>
                    Flight Models
                  </Typography>
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() =>
                      appendFlight({
                        Name: "",
                        Airline: "",
                        AirlineName: "",
                        DepartureCode: "",
                        DepartureName: "",
                        DepartureTime: "",
                        ArrivalCode: "",
                        ArrivalName: "",
                        ArrivalTime: "",
                        Stops: 0,
                        StopTime: "",
                        TripDuration: "",
                        StopCity: null,
                        AirlineLogoUrl: "",
                        FreeBaggage: {
                          BagCount: 0,
                          Weight: 0,
                          WeightUnit: "",
                        },
                        FlightLegs: [],
                      })
                    }
                  >
                    Add Flight
                  </Button>
                </Box>
                {flightModels.map((field, index) => (
                  <FlightModelItem
                    key={field.id}
                    control={control}
                    index={index}
                    register={register}
                    remove={removeFlight}
                    errors={errors}
                    availableAirlines={availableAirlines?.data?.airlines}
                    setValue={setValue}
                  />
                ))}
              </Box>

              {/* Price Details */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>
                    Price Details
                  </Typography>
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() =>
                      appendPrice({
                        BaseFare: {
                          Amount: 0,
                          CurrencyCode: getValues("currency") || "NGN",
                        },
                        PassengerType: "Adult",
                      })
                    }
                  >
                    Add Price
                  </Button>
                </Box>
                {prices.map((field, index) => (
                  <Box
                    key={field.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      position: "relative",
                    }}
                  >
                    <IconButton
                      onClick={() => removePrice(index)}
                      sx={{ position: "absolute", top: 8, right: 8 }}
                      color="error"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormInput
                          label="Amount"
                          type="number"
                          {...register(`PriceDetails.${index}.BaseFare.Amount`)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormInput
                          label="Currency"
                          {...register(
                            `PriceDetails.${index}.BaseFare.CurrencyCode`,
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormInput
                          label="Passenger Type"
                          {...register(`PriceDetails.${index}.PassengerType`)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>

              {/* Addons */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>
                    Addons (IDs)
                  </Typography>
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => appendAddon("")}
                  >
                    Add Addon
                  </Button>
                </Box>
                {addons.map((field, index) => (
                  <Box
                    key={field.id}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name={`addons_id.${index}`}
                        control={control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <AddonAutocomplete
                            label={`Addon #${index + 1}`}
                            value={value}
                            onChange={(newValue) => onChange(newValue?._id)}
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    </Box>
                    <IconButton
                      onClick={() => removeAddon(index)}
                      color="error"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 3 }}>
                {errorMsg && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMsg}
                  </Alert>
                )}
                {successMsg && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {successMsg}
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    bgcolor: "#0b1c12",
                    "&:hover": { bgcolor: "#08150e" },
                  }}
                >
                  Create Booking
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default AddManualBooking;
